import React, { FC, useCallback, useEffect, useState } from 'react'
import { Dialog } from 'primereact/dialog'
import { useNavigate } from 'react-router-dom'
import { useToast } from '../../../../common/toast/hooks/useToast'
import { ClientIdentity, ClientIdentityFormProps } from './ClientIdentity.d'
import { Card } from 'primereact/card'
import { api } from '../../../../../auth'
import Common from '../../../../common/Common'
import { Button, Alert, Row, Col, Form } from 'react-bootstrap'
import { useAppSelector } from '../../../../../store/hooks'
import PageHeader from '../../../../layout/page/PageHeader'
import { Language } from '../../../../../features/security/security.slice'
import { Formik, Form as FormikForm } from 'formik'
import * as Yup from 'yup'
import TextField from '../../../../common/forms/fields/TextField'
import DeleteDialog from '../../../../common/dialogs/DeleteDialog'
import { handleAxiosErrors } from '../../../../common/forms/errors/Helper'
import RenderFieldsErrors from '../../../../common/forms/errors/RenderFieldsErrors'
import RenderTranslatedFields from '../../../../common/forms/fields/translated-fields/RenderTranslatedFields'
import { getGroupTranslation } from '../../../../common/utility/helpers'
import FormActions from '../../../../common/forms/FormActions'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
    faCircleNotch,
    faHandPointRight,
} from '@fortawesome/free-solid-svg-icons'
import RenderFormikErrors from '../../../../common/forms/errors/RenderFormikErrors'

const ClientIdentityForm: FC<ClientIdentityFormProps> = ({
    id = null,
    inPopup = true,
    config = {},
}) => {
    const isViewOrEdit = id !== null
    const pageCode = isViewOrEdit ? 'CLIENT_IDENTITY_' + id : 'TMPSET'
    const theme = useAppSelector((state) => state.theme.theme)
    const initData = useAppSelector((state: any) => state.security.initData)

    const navigate = useNavigate()
    const axiosPrivate = api

    const emptyClientIdentity: ClientIdentity = {
        name: '',
        business_name: '',
        business_slogan: '',
        business_short_description: '',
        active: false,
        default: false,
    }

    const [clientIdentityDialog, setClientIdentityDialog] = useState(true)
    const [clientIdentity, setClientIdentity] =
        useState<ClientIdentity>(emptyClientIdentity)
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [isLoading, setIsLoading] = useState(true)
    const [errMsg, setErrMsg] = useState('')
    const [errorFields, setErrorFields] = useState<any>({})

    let schema: Record<string, any> = {
        name: Yup.string().trim().required('Enter Identity Name'),
        business_name: Yup.string().trim().required('Required'),
    }

    let _initialValues: Record<string, any> = {
        ...clientIdentity,
        business_name: pageCode + '_BUSINESS_NAME',
        business_slogan: pageCode + '_BUSINESS_SLOGAN',
        business_short_description: pageCode + '_BUSINESS_SHORT_DESCRIPTION',
    }

    ;(Object.values(initData.languages.data) as Language[]).map((lang) => {
        schema[`business_name_${lang.id}`] = Yup.string()
            .trim()
            .required(`The Business Name (${lang.name}) field is required`)
        _initialValues[`business_name_${lang.id}`] = ''
        return true
    })

    const [initialValues, setInitialValues] = useState(_initialValues)

    const ClientIdentityFormSchema = Yup.object().shape(schema)

    const { triggerToast } = useToast()

    const loadPage = useCallback(async () => {
        setErrMsg('')
        setErrorFields({})
        setIsLoading(true)

        const routePath = isViewOrEdit
            ? '/administration/client/identity/' + id
            : '/administration/client/identity/new'

        try {
            const response = await axiosPrivate.get(routePath).finally(() => {
                setIsLoading(false)
            })
            if (response.data?.success === true) {
                if (response.data.data?.identity) {
                    const identity = response.data.data.identity
                    const translations = response.data.data.translations || {}
                    setClientIdentity(identity)

                    const translationCodes = [
                        { key: 'business_name', code: identity.business_name },
                        { key: 'business_slogan', code: identity.business_slogan },
                        { key: 'business_short_description', code: identity.business_short_description },
                    ]
                    const nextInitial: Record<string, unknown> = {
                        ...initialValues,
                        ...identity,
                    }
                    Object.keys(initData.languages.data).forEach((key) => {
                        const lang: Language = initData.languages.data[key] as Language
                        const locale = lang?.locale ?? key
                        const groupTranslations: Record<string, any> = getGroupTranslation(
                            translations,
                            translationCodes,
                            locale
                        )
                        nextInitial[`business_name_${lang.id}`] = groupTranslations['business_name']
                        nextInitial[`business_slogan_${lang.id}`] = groupTranslations['business_slogan']
                        nextInitial[`business_short_description_${lang.id}`] = groupTranslations['business_short_description']
                    })
                    setInitialValues(nextInitial as typeof initialValues)
                }
            } else {
                setErrMsg(response.data?.errors.join('<br />'))
            }
        } catch (err: any) {
            // TODO: Sort out error handling!
            if (!err?.response) {
                console.log('err: ', err)
                setErrMsg('No Server Response')
            } else if (err.response?.status === 404) {
                setErrMsg('Record was not found')
            } else {
                setErrMsg('Failed to load client identity')
            }
        }
    }, [axiosPrivate, id, initData.languages, isViewOrEdit])

    useEffect(() => {
        loadPage()
    }, [loadPage])

    const saveRecord = async (record: any) => {
        setErrMsg('')
        setIsSubmitting(true)

        try {
            const routePath = isViewOrEdit
                ? '/administration/client/identity/' + id
                : '/administration/client/identity/create'
            const response = await axiosPrivate
                .post(routePath, record, {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                })
                .finally(() => {
                    setIsSubmitting(false)
                })

            if (response.data?.success === true) {
                triggerToast({
                    severity: 'success',
                    summary: config?.submitSuccessMsg?.header || 'Successful',
                    detail:
                        config?.submitSuccessMsg?.message ||
                        'Processed successfully',
                })
                hideDialog()
            } else {
                setErrMsg(response.data?.errors.join('<br />'))
            }
        } catch (err) {
            handleAxiosErrors(err, setErrMsg, setErrorFields as any, config as any)
        }
    }

    const hideDialog = () => {
        setClientIdentityDialog(false)
        navigate('/administration/client/identity')
    }

    const formFooter = (
        <React.Fragment>
            <Button
                variant={theme === 'dark' ? 'dark' : 'light'}
                onClick={hideDialog}
            >
                <span className="small">Cancel</span>
            </Button>
            {isViewOrEdit && (
                <DeleteDialog
                    url={'/administration/client/identity/delete/' + id}
                    record={clientIdentity}
                    deletedCallback={() => hideDialog()}
                    btnLabel="Delete"
                    showIcon={false}
                    confirmText={clientIdentity.name}
                    message={
                        <>
                            <p className="mb-3 pb-3 border-bottom">
                                Are you sure you want to delete this identity?
                            </p>
                            <Alert variant="warning" className="border-0">
                                <strong>Warning!</strong> All themes related to
                                this identity will be deleted permenantly!
                            </Alert>
                        </>
                    }
                />
            )}
            <Button
                variant="success"
                disabled={isSubmitting || isLoading}
                // onClick={saveRecord}
                onClick={() => {
                    const formSubmitGtn = document.getElementById(
                        'clientIdentity-form-submit-btn'
                    )
                    formSubmitGtn?.click()
                }}
            >
                <span className="small">
                    {isSubmitting ? (
                        <>
                            <FontAwesomeIcon icon={faCircleNotch} spin={true} />{' '}
                            {config?.saveButton?.savingLabel || 'Processing...'}
                        </>
                    ) : (
                        config?.saveButton?.label || 'Process'
                    )}
                </span>
            </Button>
        </React.Fragment>
    )

    const FormContent = (
        <>
            {isLoading && <Common.Loader text="Loading..." />}
            {errMsg && (
                <Alert show={true} variant="danger">
                    <Alert.Heading className="d-inline-block me-2">
                        Error!
                    </Alert.Heading>
                    <RenderFieldsErrors
                        errorFields={errorFields}
                        errMsg={errMsg}
                    />
                </Alert>
            )}
            <Formik
                initialValues={initialValues}
                validationSchema={ClientIdentityFormSchema}
                onSubmit={(values) => {
                    saveRecord(values)
                }}
                enableReinitialize={true}
            >
                {(formikProps: any) => {
                    return (
                        <FormikForm id="clientIdentity-form">
                            <RenderFormikErrors formikProps={formikProps} />
                            <Row>
                                <Col sm={12}>
                                    <fieldset>
                                        <legend>General</legend>
                                        <Row>
                                            <Col xl={6}>
                                                <TextField
                                                    id="formClientIdentityName"
                                                    name="name"
                                                    label="Identity Name"
                                                    placeholder="Enter Identity Name"
                                                    formikProps={formikProps}
                                                    required
                                                />
                                            </Col>
                                            <Col xl={12}>
                                                <RenderTranslatedFields
                                                    id="clientIdentityTranslatedFields"
                                                    formikProps={formikProps}
                                                    fields={[
                                                        {
                                                            id: 'formClientIdentityBusinessName',
                                                            type: 'text',
                                                            name: 'business_name',
                                                            label: 'Business Name',
                                                            placeholder:
                                                                'Enter Business Name',
                                                            required: true,
                                                        },
                                                        {
                                                            id: 'formClientIdentityBusinessSlogan',
                                                            type: 'text',
                                                            name: 'business_slogan',
                                                            label: 'Business Slogan',
                                                            placeholder:
                                                                'Enter Business Slogan',
                                                        },
                                                        {
                                                            id: 'formClientIdentityBusinessShortDescription',
                                                            type: 'textarea',
                                                            name: 'business_short_description',
                                                            label: 'Business Short Description',
                                                            placeholder:
                                                                'Enter Business Short Description',
                                                        },
                                                    ]}
                                                />
                                            </Col>
                                        </Row>
                                    </fieldset>
                                    <fieldset>
                                        <legend>Status</legend>
                                        <Row>
                                            <Col xl={6}>
                                                <Form.Group
                                                    controlId={
                                                        'formClientIdentityActiveFlag'
                                                    }
                                                >
                                                    <Form.Switch
                                                        type="checkbox"
                                                        label="Active"
                                                        name="active"
                                                        checked={
                                                            formikProps.values
                                                                .active
                                                        }
                                                        onChange={(e) =>
                                                            formikProps.setFieldValue(
                                                                'active',
                                                                e.target.checked
                                                            )
                                                        }
                                                    />
                                                </Form.Group>
                                            </Col>
                                            <Col xl={6}>
                                                <Form.Group
                                                    controlId={
                                                        'formClientIdentityDefaultFlag'
                                                    }
                                                >
                                                    <Form.Switch
                                                        type="checkbox"
                                                        label="Default"
                                                        name="default"
                                                        checked={
                                                            formikProps.values
                                                                .default
                                                        }
                                                        onChange={(e) =>
                                                            formikProps.setFieldValue(
                                                                'default',
                                                                e.target.checked
                                                            )
                                                        }
                                                    />
                                                </Form.Group>
                                            </Col>
                                            <Col xl={12}>
                                                <Alert
                                                    variant={
                                                        formikProps.values
                                                            .default
                                                            ? 'warning'
                                                            : 'info'
                                                    }
                                                    className={
                                                        (formikProps.values
                                                            .default
                                                            ? 'warning'
                                                            : 'info') +
                                                        ' mt-3 mb-1'
                                                    }
                                                >
                                                    <FontAwesomeIcon
                                                        icon={faHandPointRight}
                                                        className="me-2"
                                                    />{' '}
                                                    When a identity is set to{' '}
                                                    <strong>default</strong> the
                                                    system will automatically
                                                    mark it as{' '}
                                                    <strong>Active</strong>
                                                </Alert>
                                            </Col>
                                        </Row>
                                    </fieldset>
                                </Col>
                            </Row>
                            <input
                                type="submit"
                                id="clientIdentity-form-submit-btn"
                                className="d-none"
                            />
                        </FormikForm>
                    )
                }}
            </Formik>
        </>
    )

    return (
        <>
            {inPopup ? (
                <Card className="flex justify-content-center">
                    <Dialog
                        visible={clientIdentityDialog}
                        breakpoints={{ '960px': '100vw', '640px': '100vw' }}
                        style={{ width: '50vw' }}
                        header={
                            isViewOrEdit
                                ? isLoading
                                    ? 'Loading...'
                                    : 'Edit ' + clientIdentity.name
                                : 'Create new brand identity'
                        }
                        modal
                        className="p-fluid"
                        footer={formFooter}
                        onHide={hideDialog}
                        maximizable
                        contentClassName={isLoading ? 'wrapped-content' : ''}
                        closeOnEscape={false}
                    >
                        {FormContent}
                    </Dialog>
                </Card>
            ) : (
                <>
                    <PageHeader
                        header={
                            isViewOrEdit
                                ? isLoading
                                    ? 'Loading...'
                                    : 'Edit ' + clientIdentity.name
                                : 'Create new brand identity'
                        }
                    />
                    {FormContent}
                    <FormActions formFooter={formFooter} />
                </>
            )}
        </>
    )
}

export default ClientIdentityForm
